// crewbit-admin/src/components/money/TaxPrepCard.tsx
//
// SPEC §Overview.7.2 + §Overview.D5 path (a). Situational card. Visibility:
//   summary.days_until_due !== null
//   AND summary.days_until_due <= 60
//   AND summary.status === 'open'
//
// `status === 'open'` check is permanently true in v1 — the live RPC body
// hardcodes `'status', 'open'` (Session 119 §2.1a Finding 1). Filed as
// `§FinancialSummaryTaxStatus` deferred — once the RPC consults
// `tax_remittances`, State 11 ("Tax remitted → card hides") starts working.
// Mobile TaxCard (Session 118) ships with the same gap.
//
// CTAs (`Generate`, `OpenMBA`, `MarkRemitted`) are spec'd but deferred to
// the §Tax destination session.
//
// Currency boundary: `formatMoney`.

import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FinancialSummary } from '../../hooks/useFinancialSummary';
import { formatMoney } from '../../lib/money';
import { admin, palette, radii, shadow, spacing, transition } from '../../styles/tokens';

type TaxSummary = FinancialSummary['tax_summary'];

interface TaxPrepCardProps {
  summary: TaxSummary;
}

function shouldRenderTaxPrepCard(summary: TaxSummary): boolean {
  if (summary.days_until_due === null) return false;
  if (summary.days_until_due > 60) return false;
  if (summary.status !== 'open') return false;
  return true;
}

function formatDueDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface BreakdownRowProps {
  label: string;
  value: string;
}

function BreakdownRow({ label, value }: BreakdownRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: spacing.xs,
        paddingBottom: spacing.xs,
      }}
    >
      <span style={{ fontSize: 13, color: admin.body }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: admin.header,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function TaxPrepCard({ summary }: TaxPrepCardProps) {
  const navigate = useNavigate();
  if (!shouldRenderTaxPrepCard(summary)) return null;

  const daysWord = summary.days_until_due === 1 ? 'day' : 'days';
  const subtitle = `due in ${summary.days_until_due} ${daysWord} · ${formatDueDate(summary.due_date)}`;

  return (
    <section
      role="button"
      tabIndex={0}
      onClick={() => void navigate('/money/tax')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void navigate('/money/tax');
        }
      }}
      aria-label={`${summary.quarter_label} HST tax prep`}
      style={{
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: admin.cardRadius,
        padding: spacing.lg,
        marginTop: spacing.lg,
        boxShadow: shadow.card,
        cursor: 'pointer',
        transition: `box-shadow ${transition.fast}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = shadow.cardHover)}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = shadow.card)}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          marginBottom: spacing.md,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: radii.sm,
            background: palette.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileText size={16} color={palette.amber} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
            {summary.quarter_label} HST
          </div>
          <div style={{ fontSize: 12, color: palette.amber, marginTop: 2, opacity: 0.85 }}>
            {subtitle}
          </div>
        </div>
      </header>

      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: palette.amber,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          marginBottom: spacing.md,
        }}
      >
        {formatMoney(summary.net_owing)}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          background: palette.white,
          borderRadius: radii.sm,
          padding: `${spacing.sm}px ${spacing.md}px`,
        }}
      >
        <BreakdownRow label="HST collected" value={formatMoney(summary.hst_collected)} />
        <div style={{ height: 1, background: palette.g100 }} />
        <BreakdownRow label="ITCs (Box 108)" value={formatMoney(summary.itcs)} />
      </div>
    </section>
  );
}
